// `/checkout`'S ACCOUNT GATE (ACC-3a), WITH THE SERVER'S WEB_ACCOUNTS_ENABLED ON. Off, `/checkout` is today's, untouched.
//
// THREE OUTCOMES, THE SERVER'S OWN (functions/src/v2/checkout/handler.ts `checkoutAccountGate`, ACC-1):
//   a look-around, or nobody  → "Create your account" (`/account`), where C8 says why;
//   a password account whose email is not confirmed → C7 here: "Confirm your email to continue", with "Send it again"
//                               and "Change email";
//   an Apple account, or a confirmed password account → the checkout.
// THE SERVER ENFORCES IT; THIS FOLLOWS IT. The page reads the same two facts the server reads from the ID token — how the
// caller signed in, and `email_verified` — so it shows the gate before the owner presses Buy rather than after. When the
// server disagrees (a token older than a confirmation, a sign-in that ended elsewhere) its code wins: `create`'s 403
// `ACCOUNT_REQUIRED` sends the owner to create an account, and 403 `EMAIL_NOT_VERIFIED` puts C7 up, whatever the page
// believed (after re-reading the sign-in, so a sign-in that ended elsewhere is found). `test/accountScreens.test.tsx`
// holds all three, and the server's two codes, on the fake.
//
// AND THE CURRENT TERMS ON FILE (R-pending-consent-gate; Tyler, 2026-10-14: "yes and yes.."). An existing account that signed
// in through "Already have an account? Sign in" comes straight back here (`SignIn.tsx` `to`) without meeting the terms
// page. So the checkout this gate shows is told `requireConsent`: when the server's `legal.consentRequired` (2.30.0, on the
// `get` it already reads; present only while the server's CHECKOUT_REQUIRES_CURRENT_CONSENT_ENABLED is on and it would
// refuse the purchase for the terms) is there, the owner goes to `/account` — the assent page, for an account — and its
// "Get started" brings them back here. A CONSENT_REQUIRED from the server's `create` or `spend` goes the same way, with the
// server's sentence shown on the terms page. Consent on file, or the server's switch off: the checkout exactly as before;
// with the accounts switch off this gate is today's `<Checkout />`, untouched.
//
// "CHANGE EMAIL" IS THE SEAM'S VERIFY-BEFORE-UPDATE (`changeEmail`): re-enter the password first (C6), then our server
// sends E4 to the new address, and nothing changes until its link is opened — which also confirms it. So C7 then names
// the new address, and the owner comes back to this page as C7 asks. Opening either link elsewhere shows here when the
// owner returns to the tab: the page re-reads the sign-in on focus.

import { useEffect, useId, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { AuthRefusal } from "../../services";
import { needsEmailConfirmed, signedIn, useWebAccounts } from "../../app/accounts";
import { useSession } from "../../app/session";
import { heldText } from "../../components/heldCopy";
import { Checkout, type AccountGateCode } from "../Checkout";
import { AccountForm, AuthError, EmailField, PasswordField, Say, SayTemplate, SubmitButton } from "./parts";
import { looksLikeEmail } from "./refusalCopy";

export function CheckoutGate() {
  const webAccounts = useWebAccounts();
  const session = useSession();
  // F-ACC3a-2: arrived from a confirm-email link (`/account/action`), which says so here.
  const location = useLocation();
  const emailConfirmed = (location.state as { emailConfirmed?: unknown } | null)?.emailConfirmed === true;
  // What the server's `create` last refused with, when it disagreed with this page.
  const [serverSays, setServerSays] = useState<AccountGateCode | null>(null);

  // Until the server has said, and with the switch off, today's checkout, from its first frame.
  if (webAccounts !== true) return <Checkout />;
  if (!signedIn(session.auth)) {
    return <Navigate to="/account" replace state={{ from: "/checkout", accountRequired: true }} />;
  }
  if (needsEmailConfirmed(session.auth) || serverSays === "EMAIL_NOT_VERIFIED") {
    return (
      <ConfirmEmail
        onConfirmed={() => {
          setServerSays(null);
          session.refresh();
        }}
      />
    );
  }
  return (
    <>
    {emailConfirmed && (
      <div className="note good" role="status" data-held-copy="account.confirm.done">
        {heldText("account.confirm.done")}
      </div>
    )}
    <Checkout
      requireConsent
      onAccountGate={(code) => {
        // Re-read the sign-in first — a fresh token, and the user as Firebase now has it — then follow the code.
        // ACCOUNT_REQUIRED: if the sign-in has in fact ended, the guard above now sends the owner to create an account;
        // if the page is still signed in, the server's own sentence (C8) stands where the owner pressed Buy, and a
        // second press goes with the fresh token. EMAIL_NOT_VERIFIED: C7, whatever this page believed.
        void session.auth.refresh().then(() => {
          setServerSays(code);
          session.refresh();
        });
      }}
    />
    </>
  );
}

/** C7. */
function ConfirmEmail({ onConfirmed }: { onConfirmed: () => void }) {
  const session = useSession();
  const { auth } = session;
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [changing, setChanging] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState<"resend" | "change" | null>(null);
  const headingId = useId();
  const changeHeadingId = useId();

  // COME BACK TO THIS PAGE (C7's own instruction): re-read the sign-in, and pass once the email is confirmed.
  useEffect(() => {
    let alive = true;
    const check = () => {
      void auth.refresh().then(() => {
        if (alive && !needsEmailConfirmed(auth)) onConfirmed();
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [auth, onConfirmed]);

  const resend = async () => {
    if (working !== null) return;
    setRefusal(null);
    setSent(false);
    setWorking("resend");
    try {
      const result = await auth.sendEmailVerification();
      if (result.ok) return setSent(true);
      if (result.refusal.kind === "alreadyVerified") {
        await auth.refresh();
        return onConfirmed();
      }
      setRefusal(result.refusal);
    } finally {
      setWorking(null);
    }
  };

  const change = async () => {
    if (working !== null) return;
    setRefusal(null);
    setSent(false);
    const typed = newEmail.trim();
    if (!looksLikeEmail(typed)) return setRefusal({ kind: "notAnEmail" });
    setWorking("change");
    try {
      const proved = await auth.reauthenticate({ password });
      if (!proved.ok) return setRefusal(proved.refusal);
      const changed = await auth.changeEmail(typed);
      if (!changed.ok) return setRefusal(changed.refusal);
      setPendingEmail(typed);
      setChanging(false);
      setPassword("");
      setSent(true);
    } finally {
      setWorking(null);
    }
  };

  return (
    <section className="account-page confirm-email" aria-labelledby={headingId} data-account-screen="confirm-email">
      <span className="hero-tile" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28" focusable="false">
          <rect x="2.5" y="5" width="19" height="14" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3.5 6.5l8.5 6.5 8.5-6.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      </span>
      <h1 id={headingId} data-held-copy="account.confirm.heading">{heldText("account.confirm.heading")}</h1>
      <SayTemplate id="account.confirm.body" values={{ email: pendingEmail ?? auth.email ?? "" }} className="account-lead" />

      <div role="status" className="live-slot">
        {sent && (
          <div className="note good">
            <Say id="account.confirm.sent" className="copy" />
          </div>
        )}
      </div>
      {!changing && <AuthError refusal={refusal} />}

      {!changing && (
        <div className="account-actions">
          <button
            type="button"
            className="btn primary block"
            data-held-copy="account.confirm.resend"
            disabled={working !== null}
            aria-busy={working === "resend" || undefined}
            onClick={() => void resend()}
          >
            {working === "resend" && <span className="spinner dark" aria-hidden="true" />}
            <span>{heldText("account.confirm.resend")}</span>
          </button>
          <button
            type="button"
            className="btn ghost block"
            data-held-copy="account.confirm.changeEmail"
            disabled={working !== null}
            onClick={() => {
              setRefusal(null);
              setChanging(true);
            }}
          >
            {heldText("account.confirm.changeEmail")}
          </button>
        </div>
      )}

      {changing && (
        <div className="account-card change-email" data-change-email>
          <h2 id={changeHeadingId} className="vh" data-held-copy="account.confirm.changeEmail">{heldText("account.confirm.changeEmail")}</h2>
          {/* C6: the re-entry before changing an email. */}
          <Say id="account.error.reauthPassword" className="muted" />
          <AccountForm onSubmit={() => void change()} label={changeHeadingId}>
            <EmailField value={newEmail} onChange={setNewEmail} invalid={refusal?.kind === "notAnEmail"} autoFocus />
            <PasswordField value={password} onChange={setPassword} autoComplete="current-password" />
            <AuthError refusal={refusal} />
            <SubmitButton id="account.confirm.changeEmail" busy={working === "change"} />
          </AccountForm>
        </div>
      )}
    </section>
  );
}
